/**
 * WalletMigration - Migrate data from old mock wallet to real Privy wallet
 *
 * Sprint 13: When users upgrade from mock wallet to Privy embedded wallet,
 * their existing Store data needs to migrate to the new wallet address namespace.
 *
 * The old mock wallet stored its address in localStorage under 'ownyou_wallet'.
 * Store data was in IndexedDB database named 'ownyou_{address.slice(0,8)}'.
 */

const MOCK_WALLET_KEY = 'ownyou_wallet';
const MIGRATION_DONE_KEY = 'ownyou_migration_done';

export interface MigrationResult {
  success: boolean;
  itemsMigrated: number;
  fromAddress: string;
  toAddress: string;
  error?: string;
}

export class WalletMigration {
  /**
   * Check if user has data under an old mock wallet address.
   * Returns the mock address if found, null otherwise.
   */
  detectMockWalletData(): { found: boolean; mockAddress: string | null } {
    // Already migrated?
    if (localStorage.getItem(MIGRATION_DONE_KEY)) {
      return { found: false, mockAddress: null };
    }

    const raw = localStorage.getItem(MOCK_WALLET_KEY);
    if (!raw) {
      return { found: false, mockAddress: null };
    }

    try {
      const parsed = JSON.parse(raw);
      if (parsed?.address && typeof parsed.address === 'string') {
        return { found: true, mockAddress: parsed.address };
      }
    } catch {
      // Invalid JSON — not a mock wallet
    }

    return { found: false, mockAddress: null };
  }

  /**
   * Migrate Store data from mock wallet namespace to real wallet namespace.
   *
   * Opens the old IndexedDB database, reads all records, and writes them
   * to the new database with the new userId (wallet address).
   */
  async migrateData(fromAddress: string, toAddress: string): Promise<MigrationResult> {
    const result: MigrationResult = {
      success: false,
      itemsMigrated: 0,
      fromAddress,
      toAddress,
    };

    if (fromAddress === toAddress) {
      result.success = true;
      return result;
    }

    const oldDbName = `ownyou_${fromAddress.slice(0, 8)}`;
    const newDbName = `ownyou_${toAddress.slice(0, 8)}`;

    try {
      // Check if old database exists by trying to open it
      const oldDb = await this.openDB(oldDbName);
      if (!oldDb) {
        // No old database — nothing to migrate
        result.success = true;
        this.markMigrationDone(fromAddress, toAddress);
        return result;
      }

      // Read all records from old database
      const records = await this.readAllRecords(oldDb);
      oldDb.close();

      if (records.length === 0) {
        result.success = true;
        this.markMigrationDone(fromAddress, toAddress);
        return result;
      }

      // Open (or create) new database
      const newDb = await this.openOrCreateDB(newDbName);

      // Write records to new database with updated userId
      for (const record of records) {
        const newKey = `${record.namespace}::${toAddress}::${record.itemKey}`;
        await this.putRecord(newDb, {
          key: newKey,
          namespace: record.namespace,
          userId: toAddress,
          value: record.value,
          createdAt: record.createdAt,
          updatedAt: Date.now(),
        });
        result.itemsMigrated++;
      }

      newDb.close();
      result.success = true;
      this.markMigrationDone(fromAddress, toAddress);
    } catch (err) {
      result.error = err instanceof Error ? err.message : 'Unknown migration error';
    }

    return result;
  }

  /**
   * Clean up old mock wallet data after successful migration.
   */
  async cleanupMockData(mockAddress: string): Promise<void> {
    // Remove old localStorage entry
    localStorage.removeItem(MOCK_WALLET_KEY);

    // Delete old IndexedDB database
    const oldDbName = `ownyou_${mockAddress.slice(0, 8)}`;
    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.deleteDatabase(oldDbName);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Open an existing IndexedDB database. Returns null if it doesn't exist
   * or has no 'memories' object store.
   */
  private openDB(dbName: string): Promise<IDBDatabase | null> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, 1);

      request.onupgradeneeded = () => {
        // If upgrade is triggered, the database didn't exist before.
        // We don't want to create it, so close and delete.
        const db = request.result;
        db.close();
        indexedDB.deleteDatabase(dbName);
        resolve(null);
      };

      request.onsuccess = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('memories')) {
          db.close();
          resolve(null);
          return;
        }
        resolve(db);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Open or create a database with the memories object store.
   */
  private openOrCreateDB(dbName: string): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, 1);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('memories')) {
          const store = db.createObjectStore('memories', { keyPath: 'key' });
          store.createIndex('by-namespace-user', ['namespace', 'userId']);
          store.createIndex('by-namespace', 'namespace');
          store.createIndex('by-user', 'userId');
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Read all records from the memories object store.
   */
  private readAllRecords(db: IDBDatabase): Promise<Array<{
    namespace: string;
    userId: string;
    itemKey: string;
    value: unknown;
    createdAt: number;
  }>> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction('memories', 'readonly');
      const store = tx.objectStore('memories');
      const request = store.getAll();

      request.onsuccess = () => {
        const records = (request.result as Array<{
          key: string;
          namespace: string;
          userId: string;
          value: unknown;
          createdAt: number;
        }>).map((record) => {
          // Extract the original item key from composite key: namespace::userId::itemKey
          const parts = record.key.split('::');
          const itemKey = parts.slice(2).join('::'); // Rejoin in case key contains ::
          return {
            namespace: record.namespace,
            userId: record.userId,
            itemKey,
            value: record.value,
            createdAt: record.createdAt,
          };
        });
        resolve(records);
      };

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Put a record into the memories object store.
   */
  private putRecord(db: IDBDatabase, record: {
    key: string;
    namespace: string;
    userId: string;
    value: unknown;
    createdAt: number;
    updatedAt: number;
  }): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction('memories', 'readwrite');
      const store = tx.objectStore('memories');
      const request = store.put(record);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Mark migration as complete so it won't run again.
   */
  private markMigrationDone(fromAddress: string, toAddress: string): void {
    localStorage.setItem(MIGRATION_DONE_KEY, JSON.stringify({
      from: fromAddress,
      to: toAddress,
      completedAt: new Date().toISOString(),
    }));
  }
}
